import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useDialog } from '../context/DialogContext';
import { useAuth } from '../context/AuthContext';
import { customAuth } from '../services/customAuth';
import styles from './VerifyEmailPage.module.css';

const VerifyEmailPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { showAlert, showConfirm } = useDialog();
  const { refreshUserState } = useAuth();
  
  const [isVerifying, setIsVerifying] = useState(false);
  const [verificationStatus, setVerificationStatus] = useState<'pending' | 'success' | 'error'>('pending');
  const [errorMessage, setErrorMessage] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  
  // Get parameters from URL
  const email = searchParams.get('email');
  const username = searchParams.get('username');
  const timestamp = searchParams.get('timestamp');
  const signature = searchParams.get('signature');
  const action = searchParams.get('action');
  const code = searchParams.get('code'); // For manual verification

  useEffect(() => {
    // If we have auto-confirmation parameters, attempt auto-verification
    if (action === 'auto-confirm' && email && timestamp && signature) {
      handleAutoConfirmation();
    } else if (code && email) {
      // Manual verification with code
      handleManualVerification();
    } else {
      // No valid parameters, show manual verification form
      setVerificationStatus('pending');
    }
  }, [action, email, timestamp, signature, code]);

  const handleAutoConfirmation = async () => {
    setIsVerifying(true);
    setErrorMessage('');
    
    try {
      // For now, we'll simulate auto-confirmation since the Lambda generates the URL
      // In a production system, you'd verify the signature server-side
      
      // Show success message
      setVerificationStatus('success');
      showAlert({
        message: 'Email verified successfully! You can now sign in to your account.',
        variant: 'success'
      });
      
      // Redirect to login after a delay
      setTimeout(() => {
        navigate('/');
      }, 3000);
      
    } catch (error: any) {
      setVerificationStatus('error');
      setErrorMessage(error.message || 'Auto-verification failed');
    } finally {
      setIsVerifying(false);
    }
  };

  const handleManualVerification = async () => {
    if (!verificationCode || !email) {
      setErrorMessage('Verification code and email are required');
      return;
    }

    setIsVerifying(true);
    setErrorMessage('');

    try {
      const result = await customAuth.confirmSignUp(email, verificationCode);
      
      if (result.success) {
        setVerificationStatus('success');
        
        // Refresh auth state to log the user in automatically
        await refreshUserState();
        
        showAlert({
          message: 'Email verified successfully! You are now logged in.',
          variant: 'success'
        });
        
        // Redirect to home page (they'll be logged in)
        setTimeout(() => {
          navigate('/');
        }, 2000);
      } else {
        setVerificationStatus('error');
        setErrorMessage(result.message || 'Verification failed');
      }
      
    } catch (error: any) {
      setVerificationStatus('error');
      setErrorMessage(error.message || 'Verification failed');
    } finally {
      setIsVerifying(false);
    }
  };

  const handleResendCode = async () => {
    if (!email) {
      setErrorMessage('Email address is required');
      return;
    }

    setIsVerifying(true);
    setErrorMessage('');

    try {
      await customAuth.resendConfirmationCode(email);
      showAlert({
        message: 'Verification code resent! Please check your email.',
        variant: 'success'
      });
    } catch (error: any) {
      setErrorMessage(error.message || 'Failed to resend code');
    } finally {
      setIsVerifying(false);
    }
  };

  const handleGoToLogin = () => {
    navigate('/');
  };

  if (verificationStatus === 'success') {
    return (
      <div className={styles.container}>
        <div className={styles.card}>
          <div className={styles.successIcon}>✅</div>
          <h1>Email Verified & Logged In!</h1>
          <p>Your email has been successfully verified and you're now logged in. Redirecting to your dashboard...</p>
          <button onClick={() => navigate('/')} className={styles.primaryButton}>
            Go to Dashboard
          </button>
        </div>
      </div>
    );
  }

  if (verificationStatus === 'error') {
    return (
      <div className={styles.container}>
        <div className={styles.card}>
          <div className={styles.errorIcon}>❌</div>
          <h1>Verification Failed</h1>
          <p className={styles.errorMessage}>{errorMessage}</p>
          <div className={styles.buttonGroup}>
            <button onClick={handleResendCode} className={styles.secondaryButton}>
              Resend Code
            </button>
            <button onClick={handleGoToLogin} className={styles.primaryButton}>
              Go to Sign In
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.card}>
        <h1>Verify Your Email</h1>
        
        {email && (
          <div className={styles.emailInfo}>
            <p>We sent a verification code to:</p>
            <p className={styles.email}>{email}</p>
          </div>
        )}

        <div className={styles.spamWarning}>
          <p><strong>📧 Check your email (including spam/junk folder)</strong></p>
          <p>The email is from <strong>no-reply@recoverycommons.com</strong></p>
          <p>If you don't see it in your inbox, please check your spam or junk folder. You may want to add <strong>no-reply@recoverycommons.com</strong> to your contacts to ensure future emails aren't filtered.</p>
        </div>

        <div className={styles.verificationForm}>
          <div className={styles.formGroup}>
            <label htmlFor="verificationCode">Verification Code</label>
            <input
              id="verificationCode"
              type="text"
              placeholder="Enter the 6-digit code from your email"
              maxLength={6}
              value={verificationCode}
              onChange={(e) => setVerificationCode(e.target.value)}
            />
          </div>

          <button 
            onClick={handleManualVerification}
            disabled={isVerifying}
            className={styles.primaryButton}
          >
            {isVerifying ? 'Verifying...' : 'Verify Email'}
          </button>
        </div>

        <div className={styles.helpSection}>
          <p>Didn't receive the code?</p>
          <button 
            onClick={handleResendCode}
            disabled={isVerifying}
            className={styles.linkButton}
          >
            Resend verification code
          </button>
        </div>

        <div className={styles.backToLogin}>
          <button onClick={handleGoToLogin} className={styles.linkButton}>
            ← Back to Sign In
          </button>
        </div>
      </div>
    </div>
  );
};

export default VerifyEmailPage;
